import { getHeight, getWidth } from 'ol/extent';
import LayerGroup from 'ol/layer/Group';
import { unByKey } from 'ol/Observable';
import { PROJECTIONS as WEB_MERCATOR_PROJECTIONS } from 'ol/proj/epsg3857';
import Projection from 'ol/proj/Projection';
import View from 'ol/View';

import type { CancelableApiCallback } from 'cadenza/api-client/api';
import { singleExecution } from 'cadenza/api-client/api';
import type { DimensionDto } from 'cadenza/api-client/dto/dimension-dto';
import type { LayerDto } from 'cadenza/api-client/dto/layer-dto';
import type { MapDto } from 'cadenza/api-client/dto/map-dto';
import { isWorkbookGroupLayerDto } from 'cadenza/api-client/dto/workbook-group-layer-dto';
import type { WorldBoxDto } from 'cadenza/api-client/dto/world-box-dto';
import { getMapContext } from 'cadenza/api-client/map-context-api';
import { context } from 'cadenza/map/context/context';
import { WorkbookHighlightHandler } from 'cadenza/map/custom/workbook/workbook-highlight-handler';
import { LayerProperty } from 'cadenza/map/layer-property';
import { createLayer, scaleToResolution, updateLayer, WORKBOOK_VIEW_ON_DEMAND } from 'cadenza/map/layer-utils';
import type { Map } from 'cadenza/map/map';
import { getAllLayers } from 'cadenza/map/map-layer-utils';
import { MapProperty } from 'cadenza/map/map-property';
import {
  calculateMapViewPadding,
  fitMapViewToScaledExtent,
  resolutionForScale,
  scaleForResolution
} from 'cadenza/map/map-utils';
import { MapView } from 'cadenza/map/map-view';
import { SourceProperty } from 'cadenza/map/source-property';
import { ViewProperty } from 'cadenza/map/view-property';
import { on } from 'cadenza/utils/event-util';
import { logger } from 'cadenza/utils/logging';
import { roundToFloatOrInt } from 'cadenza/utils/math';
import { throttle } from 'cadenza/utils/throttle';
import type { LayerId } from 'cadenza/workbook/api/layer-id';
import { assertNonNullable } from 'cadenza/utils/custom-error';

import type { ViewOptions } from 'ol/View';
import type { Extent } from 'ol/extent';
import type Collection from 'ol/Collection';
import type { EventsKey } from 'ol/events';
import type Feature from 'ol/Feature';
import type BaseLayer from 'ol/layer/Base';

type WorkbookMapContextInitializationOptions = Pick<MapDto, 'projection' | 'currentExtent' | 'maxExtent' | 'minAllowedScale' | 'maxAllowedScale' | 'constrainZoomScale' | 'forceMinMaxScale'>;

const DEFAULT_NUMBER_OF_ZOOM_LEVELS = 25;
const ZOOM_SCALE_FACTOR = 2;

async function getMapContextFromApiOrWorkbookState (map: Map, contextUrl: string, dimensions: DimensionDto) {
  // TODO (solovjova, 2024-01-26, workbook-map-context): better way to differentiate between wb map views and other use cases (only the default map?)
  // Experimental workbook map context creation from the workbook redux state
  if (contextUrl?.startsWith('/workbooks/')) {
    // Dynamic import to avoid having workbook redux store dependency in the classical maps through generic context() function, can be cleaned up after epic split.
    const { getMapContextFromWorkbookReduxStore } = await import(/* webpackChunkName: "workbook-map-context-provider" */ 'cadenza/map/context/workbook/workbook-map-context-provider');
    return getMapContextFromWorkbookReduxStore(
      map.get(MapProperty.WORKBOOK_VIEW_ID),
      map.get(MapProperty.IS_CLIENT_SIDE_RENDERING)
    );
  }
  return getMapContext(contextUrl, dimensions);
}

/**
 * Please do *not* instantiate this directly and instead use cadenza/map/context, i.e. context(map)
 */
export class WorkbookMapContext {

  readonly #contextPushEnabled;
  /**
   * List of IDs of views or data views that need to be invalidated.
   */
  #changedLayerIds?: string[];
  readonly #map: Map;
  readonly #highlightHandler: WorkbookHighlightHandler;
  #eventKeys: EventsKey[] = [];
  #isPullInProgress = false;
  readonly #pushIfNotPullingThrottled = throttle(this._pushIfNotPulling.bind(this), 500);
  #isLoading = false;
  readonly #getContextFromApiSingleExecutor: CancelableApiCallback<MapDto>;
  readonly #getAutoZoomExtentFromApiSingleExecutor: CancelableApiCallback<WorldBoxDto | undefined>;

  #currentMapContext?: MapDto;

  constructor (map: Map) {
    this.#map = map;
    this.#highlightHandler = new WorkbookHighlightHandler(map);
    this.#contextPushEnabled = map.get(MapProperty.CONTEXT_PUSH_ENABLED);

    this.#getContextFromApiSingleExecutor = singleExecution((contextUrl: string, dimensions: DimensionDto) => getMapContextFromApiOrWorkbookState(map, contextUrl, dimensions));
    this.#getAutoZoomExtentFromApiSingleExecutor = singleExecution(callGetAutozoomExtentIfRelevant);
  }

  /**
   * Initial setup of map context
   *
   * @param mapExtent - Optional override of map extent obtained from server
   * @param pull - If <code>true</code>, pull map context
   * @return Promise that resolves when the pull is finished
   */
  setup (mapExtent: WorldBoxDto | undefined, pull = true) {
    const setupPromise = pull ? this.pull(mapExtent) : Promise.resolve();

    const map = this.#map;
    this.#eventKeys = [
      map.on('propertychange', (event) => {
        if (event.key === MapProperty.SIZE) {
          this.push();
        }
        if (event.key === MapProperty.PRIMARY_INTERACTION) {
          if (event.oldValue) {
            event.oldValue.setActive(false);
            map.removeInteraction(event.oldValue);
          }
          const newInteraction = map.get(MapProperty.PRIMARY_INTERACTION);
          if (newInteraction) {
            map.addInteraction(newInteraction);
            newInteraction.setActive(true);
          }
        }
      }),
      map.getLayers().on('propertychange', () => this.push()),
      map.getLayers().on('remove', ({ element: layer }) => context(layer).teardown())
    ];

    return setupPromise;
  }

  teardown () {
    unByKey(this.#eventKeys);
  }

  /**
   * Loads map metadata from the server and updates the map accordingly.
   *
   * @param mapExtent - Optional override of map extent obtained from server
   * @return Promise that resolves when the pull is finished
   */
  async pull (mapExtent?: WorldBoxDto) {
    try {
      const [ contextApiResponse, extentResponse ] = await Promise.allSettled([
        this.#getContextFromApiSingleExecutor(this.#map.get(MapProperty.CONTEXT_URL), this.#map.getInnerSize()),
        this.#getAutoZoomExtentFromApiSingleExecutor(this.#map)
      ]);
      if (!('value' in contextApiResponse)) {
        return;
      }
      const mapContext = contextApiResponse.value;
      this.#currentMapContext = mapContext;
      this.#isPullInProgress = true;
      this.#applyMapContext(mapContext, mapExtent);
      if ('value' in extentResponse) {
        this.#autoZoom(extentResponse.value);
      }
    } finally {
      this.#isPullInProgress = false;
    }
  }

  #applyMapContext (
    {
      title,
      projection,
      currentExtent,
      maxExtent,
      layers,
      minAllowedScale,
      maxAllowedScale,
      constrainZoomScale,
      forceMinMaxScale,
      defaultCoordinateVisualizationSrs
    }: MapDto,
    mapExtent?: WorldBoxDto
  ) {
    this.#map.setProperties({
      title,
      [MapProperty.DEFAULT_COORDINATE_VISUALIZATION_SRS]: defaultCoordinateVisualizationSrs,
      [MapProperty.PROJECTION_WKT]: projection.wkt
    });

    const viewIsInitialized = isMapViewInitialized(this.#map);
    if (!viewIsInitialized) {
      this._initializeView({
        projection,
        currentExtent: mapExtent || currentExtent,
        maxExtent,
        minAllowedScale,
        maxAllowedScale,
        constrainZoomScale,
        forceMinMaxScale
      });
      this._scheduleViewExtentReinitializationIfRequired(mapExtent);
    }

    this._updateLayers(layers);
  }

  #autoZoom (extent?: WorldBoxDto) {
    const viewIsInitialized = isMapViewInitialized(this.#map);
    if (viewIsInitialized && extent) {
      fitMapViewToScaledExtent(this.#map, extent);
    }
  }

  _initializeView ({
    projection: { code, units, title, usesDefaultSrs },
    currentExtent,
    maxExtent,
    minAllowedScale,
    maxAllowedScale,
    constrainZoomScale,
    forceMinMaxScale
  }: WorkbookMapContextInitializationOptions) {
    const map = this.#map;
    const projection = new Projection({ code, units });
    const {
      minResolution,
      maxResolution
    } = calculateMinMaxResolutions(projection, maxExtent, map.getInnerSize(), minAllowedScale, maxAllowedScale);
    const padding = calculateMapViewPadding(map);

    const mapOptions: ViewOptions = {
      projection,
      extent: maxExtent,
      maxResolution,
      minResolution,
      padding,
      // By default, the whole view extent is constrained to the given extent, so the user is not allowed to see anything outside the given extent.
      // Thus, always only some part of the given extent is visible. But our aim here is to display the whole given extent in the view.
      // With constrainOnlyCenter = true, the extent constraint is applied to the view center and not the whole extent.
      constrainOnlyCenter: true,
      smoothResolutionConstraint: false,
      smoothExtentConstraint: false,
      constrainResolution: !!constrainZoomScale
    };

    const minAllowedScaleSet = isMinAllowedScaleSet(minAllowedScale);
    const maxAllowedScaleSet = isMaxAllowedScaleSet(maxAllowedScale);

    if (forceMinMaxScale && (minAllowedScaleSet || maxAllowedScaleSet)) {
      // Forces open layers to really use defined resolution steps exactly.
      // This setting wins over minResolution and maxResolution options, if resolutions option is defined OL ignores min/maxResolution options.
      // Without this setting min would be inferred from max and probably not the same value as the configured min.
      // Resolution steps are calculated from the min and max resolution values, starting with max and dividing it with 2 till min is reached, adding also min at the end.
      mapOptions.resolutions = this._calcResolutions(maxResolution, minResolution);
    }

    const view = new MapView(mapOptions);
    view.set(ViewProperty.MAX_EXTENT, maxExtent);
    view.set(ViewProperty.PROJECTION_TITLE, title);
    view.set(ViewProperty.USES_DEFAULT_SRS, usesDefaultSrs);

    // Do view.fit() *before* map.setView(view)
    // so that the view already has a center when set.
    // This avoids initialization problems, e.g. with the OverviewMap.
    //
    // "nearest: true" is required to avoid zooming one step out after page refresh.
    // If exactly same extent is given to the view with exactly same size, the resolution
    // calculated by OL is not exactly the same as it was, because of rounding errors.
    // "nearest" ensures, that the best suitable constrained resolution will be set and the map will not be zoomed out to fit the extent.
    view.fit(currentExtent, { nearest: true, size: map.getInnerSize() });
    map.setView(view);

    // When there is a change in size of the view-port, recalculate the min/max resulting because
    // they depend on the view-port size.
    this.#map.on('change:size', () => {
      // We have to move the update to a different event round to not conflict with the internal state
      // update of OL's View object.
      setTimeout(() => {
        this.#updateMinMaxResolution();
      });
    });
  }

  _calcResolutions (maxResolution: number, minResolution: number) {
    const resolutions = [];
    resolutions.push(maxResolution);
    let half = roundToFloatOrInt(maxResolution / ZOOM_SCALE_FACTOR, 3);
    while (half > minResolution) {
      resolutions.push(half);
      half = roundToFloatOrInt(half / ZOOM_SCALE_FACTOR, 3);
    }
    resolutions.push(minResolution);
    return resolutions;
  }

  // A workbook map view can be initially hidden on mobile devices, where only one view is shown at a time.
  // Unfortunately all hidden workbook views are still fully initialized and do all their internal work, even if they are hidden.
  // So the map view can be initialized even if it is not displayed. In this case map has a size [0, 0], OL defaults it then to [100, 100].
  // We cannot initialize the view extent without knowing the exact map container size.
  // A workaround here is to wait for the first resize event (it means, the view was shown) and reinitialize the view extent.
  // A perfect solution would be to not load anything in the hidden views until they are shown. Then this method can be cleaned up.
  _scheduleViewExtentReinitializationIfRequired (mapExtent?: WorldBoxDto) {
    const map = this.#map;
    const { width, height } = map.getTargetElement().getBoundingClientRect();
    if (width !== 0 && height !== 0) {
      return;
    }
    // Map size 0 usually means the map is not visible (for example because the whole workbook map view is not displayed),
    // in this case we schedule map current extent recalculation and reset it as soon as window is resized.
    on(window, 'resize', () => {
      // mapExtent is the persisted last extent. In case we have mapExtent, just use it. Otherwise request the extent from the backend (by doing a lightweight version of pull request)
      const getMapExtentPromise = mapExtent
        ? Promise.resolve(mapExtent)
        : getMapContextFromApiOrWorkbookState(map, map.get(MapProperty.CONTEXT_URL), map.getInnerSize())
          .then(({ currentExtent }) => currentExtent);
      getMapExtentPromise
        .then(currentExtent => this._reinitializeViewExtent(currentExtent))
        .catch(error => logger.error('Failed to pull map context', error));
    }, { once: true, capture: false });
  }

  _reinitializeViewExtent (currentExtent: WorldBoxDto) {
    this.#updateMinMaxResolution();
    this.#map.getView()
      .fit(currentExtent, { nearest: true, size: this.#map.getInnerSize() });
  }

  #updateMinMaxResolution () {
    const map = this.#map;
    const view = map.getView();

    // Also recalculate min and max resolution as they depend on the map size.
    const {
      minResolution,
      maxResolution
    } = calculateMinMaxResolutions(
      view.getProjection(),
      view.get(ViewProperty.MAX_EXTENT),
      map.getInnerSize(),
      this.#currentMapContext!.minAllowedScale,
      this.#currentMapContext!.maxAllowedScale);
    // view.applyOptions_ is currently the only way to change the min/max-resolution without having to replace the view with a new one.
    view.applyOptions_(view.getUpdatedOptions_({ minResolution, maxResolution }));
  }

  _updateLayers (requestedLayers: LayerDto[] = []) {
    const currentLayers = this.#map.getLayers();
    this.#updateLayerCollection(currentLayers, requestedLayers);
  }

  /**
   * Updates the layer on the map
   *
   * @param currentLayers - layers currently loaded on the map (that need to be updated)
   * @param requestedLayers - layers containing the requested changes
   * @private
   */
  #updateLayerCollection (currentLayers: Collection<BaseLayer>, requestedLayers: LayerDto[]) {
    let layersChanged = false;
    const newLayers: BaseLayer[] = [];
    requestedLayers.forEach((requestedLayer) => {
      let currentLayer = findLayer(currentLayers, requestedLayer.id);

      // If layer type changed, remove it from the map and add it as a new layer
      if (currentLayer && (currentLayer.get(LayerProperty.TYPE) !== requestedLayer.type)) {
        currentLayers.remove(currentLayer);
        layersChanged = true;
        currentLayer = undefined;
      }
      if (currentLayer) {
        // Update the current layer
        const reload = !this.#changedLayerIds || this.#changedLayerIds.includes(currentLayer.get(LayerProperty.ID));
        updateLayer(this.#map, currentLayer, requestedLayer, reload);
      } else {
        // Add new layers
        const newLayer = createLayer(requestedLayer, this.#map);
        context(newLayer).setup(this.#map);
        currentLayers.push(newLayer);
        newLayers.push(newLayer);
        layersChanged = true;
      }
    });

    // Remove layers that do not exist on the map anymore.
    [ ...currentLayers.getArray() ].forEach((currentLayer) => {
      const id = layerId(currentLayer);
      if (!requestedLayers.find(layer => layer.id === id)) {
        currentLayers.remove(currentLayer);
        layersChanged = true;
      }
    });

    // Updating the order of the remaining layers
    const mapLayersInNewOrder = requestedLayers.map((layer) => findLayer(currentLayers, layer.id)!);
    const layerOrderChanged = mapLayersInNewOrder.some((layer, index) => index !== this.layers.indexOf(layer));
    if (layerOrderChanged) {
      setLayers(currentLayers, mapLayersInNewOrder);
      layersChanged = true;
    }

    if (layersChanged) {
      currentLayers.changed();
    }

    // Check if there are changes on sublayers of a layer group
    requestedLayers.forEach((layer) => {
      if (isWorkbookGroupLayerDto(layer)) {
        const sublayers = layer.layers;
        const groupLayer = findLayer(currentLayers, layer.id) as LayerGroup;
        this.#updateLayerCollection(groupLayer.getLayers(), sublayers);
      }
    });

    this.#changedLayerIds = undefined; // Reset after updateLayers().
  }

  _pushIfNotPulling () {
    return this.#isPullInProgress ? Promise.resolve() : this._push();
  }

  push () {
    return this.#pushIfNotPullingThrottled()!;
  }

  _push () {
    if (this.#contextPushEnabled) {

      // TODO (solovjova, 2024-01-24, workbook-map-context): push should update map view metadata in the workbook redux store

      // const map = this.#map;
      // return this.#mapContextApi.updateContext(
      //   {
      //     size: map.getInnerSize(),
      //     // It is important to subtract padding from the map size and the extent.
      //     // Otherwise, the map view will be shifted by the size of the toolbar on each page reload,
      //     // as padding is taken into account when fitting the map view.
      //     extent: map.getView().calculateVisibleExtent(),
      //     scale: this.mapScale,
      //     layers: this.layers.map(layerId),
      //     hiddenLayers: this.layers.filter((layer) => !layer.getVisible()).map(layerId)
      //   }
      // );
    } else {
      return Promise.resolve();
    }
  }

  getLayer (id: string) {
    return this.allLayers.find((layer) => layerId(layer) === id);
  }

  /**
   * Returns only the root layers/groups
   */
  get layers () {
    return [ ...this.#map.getLayers().getArray() ];
  }

  /**
   * Returns only the leaf layers (not the groups)
   */
  get sourceLayers () {
    return [ ...this.#map.getAllLayers() ];
  }

  /**
   * Returns all layers at all levels including groups.
   */
  get allLayers () {
    return getAllLayers(this.layers);
  }

  getCanceledLayers () {
    return this.sourceLayers.filter(layer => layer.get(LayerProperty.TASKS)?.hasRestartableTasks());
  }

  getCancellableLayers () {
    return this.sourceLayers.filter(layer => layer.get(LayerProperty.TASKS)?.hasOngoingTasks());
  }

  getOnDemandLayers () {
    // All layers returned by this.sourceLayers have a source.
    return this.sourceLayers.filter(layer => layer.getSource()!.get(SourceProperty.WORKBOOK_VIEW_ON_DEMAND) === WORKBOOK_VIEW_ON_DEMAND.ON);
  }

  /**
   * Returns the visibility state of a layer
   * with consideration of ancestor layer group visibility states
   * and layer children visibility states.
   * a layer group is visible is a minimum of one leaf layers is visible
   */
  isVisible (layer: BaseLayer): boolean {
    // Check that layer is visible and if it is a group at least one of its leafs is visible:
    if (!hasVisibleLayers([ layer ])) {
      return false;
    }
    // Check that layer and all its ancestors are visible:
    return getLayerPath(this.layers, layer).every(l => l.getVisible());
  }

  set loading (loading) {
    this.#isLoading = loading;
  }

  get loading () {
    return this.#isLoading;
  }

  cancelLoading () {
    this.layers.forEach(layer => {
      const layerTasks = layer.get(LayerProperty.TASKS);
      if (layerTasks && layerTasks.hasOngoingTasks()) {
        layerTasks.cancelTasks();
      }
    });
  }

  isLegendVisible (layer: BaseLayer): boolean {
    if (layer instanceof LayerGroup) {
      let visible = false;
      for (let sl of layer.getLayers().getArray()) {
        visible = visible || this.isLegendVisible(sl);
      }
      return visible;
    }
    return layer.getVisible() && (layer.get(LayerProperty.LEGEND_ENABLED) ?? true);
  }

  highlightFeatures (features: Feature[]) {
    this.#highlightHandler.highlightFeatures(features);
  }

  resetHighlighting () {
    this.#highlightHandler.resetHighlighting();
  }

  async zoomToLayer (layerId: LayerId) {
    // Dynamic import to avoid having workbook redux store dependency in the classical maps through generic context() function
    const { WorkbookLayerZoomToExtent } = await import(/* webpackChunkName: "workbook-layer-zoom-to-extent" */ 'cadenza/map/layer-actions/workbook-layer-zoom-to-extent');
    const layer = this.getLayer(layerId);
    assertNonNullable(layer);
    return new WorkbookLayerZoomToExtent(layer, this.#map).execute();
  }

  get mapScale () {
    const view = this.#map.getView();
    return scaleForResolution(view, view.getResolution()!);
  }

  set mapScale (scale) {
    const view = this.#map.getView();
    view.setResolution(resolutionForScale(view, scale));
  }

  getCurrentPrimaryInteractionIfResettable () {
    const primaryInteraction = this.#map.get(MapProperty.PRIMARY_INTERACTION);
    return primaryInteraction?.resettable ? primaryInteraction : null;
  }

  getProjection () {
    return this.#map.getView().getProjection();
  }

  setChangedLayerIds (changedLayerIds: string[]) {
    this.#changedLayerIds = changedLayerIds;
  }

}

function setLayers (currentLayers: Collection<BaseLayer>, layers: BaseLayer[]) {
  // this mutates the layer-array.
  const oldLayers = currentLayers.getArray();
  oldLayers.splice(0, oldLayers.length, ...layers);
}

function layerId (layer: BaseLayer) {
  return layer.get(LayerProperty.ID) as string;
}

function calculateMinMaxResolutions (
  projection: Projection,
  maxExtent: WorldBoxDto,
  mapSize: DimensionDto,
  minAllowedScale: number,
  maxAllowedScale: number) {
  // Dummy view is needed to calculate maxResolution for the real map view. There is no other way to supply maxResolution after View object construction.
  const dummyView = new View({ projection });
  let maxResolution = dummyView.getResolutionForExtent(maxExtent, mapSize);
  /*
      Based on how OL2 calculates min resolution - relevant OL2 code can be found here:
      https://github.com/openlayers/ol2/blob/master/lib/OpenLayers/Layer.js#L838
      Current calculation is based on expectation that the maxExtent option was the only relevant attribute (option)
      initially passed to OL2 in cadenza application.
  */
  let minResolution = maxResolution / Math.pow(ZOOM_SCALE_FACTOR, DEFAULT_NUMBER_OF_ZOOM_LEVELS - 1);

  // For Web Mercator projections maps, maxResolution is adjusted to match standard web mercator zoom levels and avoid map image scaling.
  // minResolution is always the same - the 25th web mercator zoom level
  if (isWebMercatorProjection(projection)) {
    const maxWebMercatorResolution = getWebMercatorMaxResolution();
    let adjustedMaxResolution = maxWebMercatorResolution;
    while (adjustedMaxResolution > maxResolution) {
      adjustedMaxResolution /= ZOOM_SCALE_FACTOR;
    }
    maxResolution = roundToFloatOrInt(adjustedMaxResolution, 3);
    minResolution = roundToFloatOrInt(maxWebMercatorResolution / Math.pow(ZOOM_SCALE_FACTOR, DEFAULT_NUMBER_OF_ZOOM_LEVELS - 1), 3);
  }
  // overwrite any calculated values if the min/maxAllowedScale is set in the mml file
  if (isMinAllowedScaleSet(minAllowedScale)) {
    minResolution = scaleToResolution(minAllowedScale);
  }
  if (isMaxAllowedScaleSet(maxAllowedScale)) {
    maxResolution = scaleToResolution(maxAllowedScale);
  }

  return { minResolution, maxResolution };
}

function isWebMercatorProjection (projection: Projection) {
  return WEB_MERCATOR_PROJECTIONS.some(p => p.getCode() === projection.getCode());
}

function getWebMercatorMaxResolution () {
  return maxResolutionFromExtent(WEB_MERCATOR_PROJECTIONS[0].getExtent());
}

/**
 * Creates max resolution from an extent. Inspired by OL function ol/tilegrid~resolutionsFromExtent.
 *
 * @param extent - Extent
 * @param [tileSize] - Tile size
 * @param [tileSize.tileWidth]
 * @param [tileSize.tileHeight]
 * @return maximal resolution
 */
function maxResolutionFromExtent (extent: Extent, { tileWidth = 256, tileHeight = 256 } = {}) {
  return Math.max(getWidth(extent) / tileWidth, getHeight(extent) / tileHeight);
}

function isMinAllowedScaleSet (minAllowedScale: number) {
  return minAllowedScale > 0;
}

function isMaxAllowedScaleSet (maxAllowedScale: number) {
  return maxAllowedScale > 0 && maxAllowedScale < Number.MAX_VALUE;
}

function findLayer (currentLayers: Collection<BaseLayer>, layerIdToFind: string) {
  return currentLayers.getArray().find((l) => layerId(l) === layerIdToFind);
}

function hasVisibleLayers (layers: BaseLayer[]) {
  for (const layer of layers) {
    if (layer.getVisible() && (!(layer instanceof LayerGroup) || hasVisibleLayers(layer.getLayersArray()))) {
      return true;
    }
  }
  return false;
}

/**
 * Returns the ancestor list of the layer (with layer itself included), which is the path of the layer
 * in the layer tree starting with a root layer node up to the layer itself down the layer tree.
 * For example for the following layer tree structure:
 *  - layer1
 *  - layer2
 *    - layer21
 *    - layer22
 *      - layer221
 *  - layer3
 *  If the function is called for the layer layer221, the result will be:
 *    [layer2, layer22, layer221]
 *  If the function is called for the layer layer1, the result will be:
 *    [layer1]
 *  In case the layer can't be found in the tree, an empty array is returned.
 */
function getLayerPath (layers: BaseLayer[], layer: BaseLayer): BaseLayer[] {
  if (layers.indexOf(layer) !== -1) {
    return [ layer ];
  }
  const layerGroups = layers.filter(l => l instanceof LayerGroup) as LayerGroup[];
  for (let layerGroup of layerGroups) {
    const subPath = getLayerPath(layerGroup.getLayers().getArray(), layer);
    if (subPath.length) {
      return [ layerGroup, ...subPath ];
    }
  }
  return [];
}

// Dynamic import to avoid having workbook redux store dependency in the classical maps through generic context() function
async function callGetAutozoomExtentIfRelevant (map: Map) {
  const { getAutozoomExtentIfRelevant } = await import(/* webpackChunkName: "context-autozoom" */ './context-autozoom');
  return getAutozoomExtentIfRelevant(map);
}

function isMapViewInitialized (map: Map) {
  return !!map.getView().getCenter();
}
